import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, GuestRoute } from './guards';

// Layout Components
import AuthLayout from '../components/layout/AuthLayout';
import RGBBackground from '../components/layout/RGBBackground';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartWishlistDrawer from '../components/common/CartWishlistDrawer';
import PurchaseTimerWidget from '../components/common/PurchaseTimerWidget';

// Lazy Loaded Pages
const Home = lazy(() => import('../pages/Home'));
const Auth = lazy(() => import('../pages/Auth'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const VerifyOtp = lazy(() => import('../pages/VerifyOtp'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const Profile = lazy(() => import('../pages/Profile'));
const AllGames = lazy(() => import('../pages/AllGames'));
const GameDetails = lazy(() => import('../pages/GameDetails'));
const Cart = lazy(() => import('../pages/Cart'));
const Checkout = lazy(() => import('../pages/Checkout'));
const Wishlist = lazy(() => import('../pages/Wishlist'));
const Blog = lazy(() => import('../pages/Blog'));
const BlogDetail = lazy(() => import('../pages/BlogDetail'));
const Contact = lazy(() => import('../pages/Contact'));
const FAQs = lazy(() => import('../pages/FAQs'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('../pages/TermsOfService'));
const Library = lazy(() => import('../pages/Library'));
const OrderHistory = lazy(() => import('../pages/OrderHistory'));
const Platforms = lazy(() => import('../pages/Platforms'));
const NotFound = lazy(() => import('../pages/NotFound'));
const Maintenance = lazy(() => import('../pages/Maintenance'));

/* ── Custom Cyber Loading Spinner for Suspense Fallback ── */
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
    <div className="w-12 h-12 rounded-full border-4 border-cyber-blue/20 border-t-cyber-blue animate-spin shadow-[0_0_15px_rgba(0,240,255,0.4)]"></div>
    <span className="text-xs font-cyber tracking-widest text-cyber-blue uppercase animate-pulse">Initializing Data Stream...</span>
  </div>
);

/* ── Main layout wrapper (header + footer + overlays) ── */
function MainLayout({ children }) {
  return (
    <div className="relative min-h-screen text-slate-100 flex flex-col justify-between selection:bg-cyber-blue/30 selection:text-white">
      <RGBBackground />
      <Header />
      <PurchaseTimerWidget />
      <main className="flex-1 w-full relative">
        {children}
      </main>
      <Footer />
      <CartWishlistDrawer />
    </div>
  );
}

function UserRoutes() {
  return (
    <Suspense>
      <Routes>
        {/* ── Public store pages (with header/footer) ── */}
        <Route path="/" element={<MainLayout><Home /></MainLayout>} />
        <Route path="/all-games" element={<MainLayout><AllGames /></MainLayout>} />
        <Route path="/platforms" element={<MainLayout><Platforms /></MainLayout>} />
        <Route path="/game/:slug" element={<MainLayout><GameDetails /></MainLayout>} />
        <Route path="/cart" element={<MainLayout><Cart /></MainLayout>} />
        <Route path="/blog" element={<MainLayout><Blog /></MainLayout>} />
        <Route path="/blog/:id" element={<MainLayout><BlogDetail /></MainLayout>} />
        <Route path="/contact" element={<MainLayout><Contact /></MainLayout>} />
        <Route path="/faq" element={<MainLayout><FAQs /></MainLayout>} />
        <Route path="/privacy" element={<MainLayout><PrivacyPolicy /></MainLayout>} />
        <Route path="/terms" element={<MainLayout><TermsOfService /></MainLayout>} />
        {/* ── Protected pages (with header/footer) ── */}
        <Route path="/profile" element={<ProtectedRoute> <MainLayout><Profile /></MainLayout> </ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute> <MainLayout><Checkout /></MainLayout> </ProtectedRoute>} />
        <Route path="/wishlist" element={<ProtectedRoute> <MainLayout><Wishlist /></MainLayout> </ProtectedRoute>} />
        <Route path="/library" element={<ProtectedRoute> <MainLayout><Library /></MainLayout> </ProtectedRoute>} />
        <Route path="/order-history" element={<ProtectedRoute> <MainLayout><OrderHistory /></MainLayout> </ProtectedRoute>} />
        {/* ── Auth pages (no header/footer) ── */}
        <Route path="/login" element={<GuestRoute> <AuthLayout><Auth /></AuthLayout> </GuestRoute>} />
        <Route path="/register" element={<Navigate to="/login" state={{ activeTab: 'register' }} replace />} />
        <Route path="/forgot-password" element={<GuestRoute> <AuthLayout><ForgotPassword /></AuthLayout> </GuestRoute>} />
        <Route path="/verify-otp" element={<AuthLayout><VerifyOtp /></AuthLayout>} />
        <Route path="/reset-password" element={<AuthLayout><ResetPassword /></AuthLayout>} />
        {/* ── Maintenance page ── */}
        <Route path="/maintenance" element={<Maintenance />} />
        {/* ── 404 fallback ── */}
        <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
      </Routes>
    </Suspense>
  );
}

export default UserRoutes;
